import axios, { AxiosError, AxiosRequestConfig, ResponseType } from "axios";
import {
  IAuthTokens,
  TokenRefreshRequest,
  applyAuthTokenInterceptor,
  getBrowserLocalStorage,
  refreshTokenIfNeeded,
} from "axios-jwt";

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestData {
  data?: any;
  headers?: Record<string, string>;
  method: Method;
  params?: Record<string, string | number>;
  responseType?: ResponseType;
  /** Uses a bare axios instance with no JWT interceptor (must pass `withNoAuthClient`) */
  noAuth?: boolean;
}

export interface HttpClientErrorHandlers {
  onResponseError?: (message: string) => void;
  onNetworkError?: (message: string) => void;
  onSetupError?: (message: string) => void;
}

export interface CreateHttpClientOptions {
  baseURL: string;
  withNoAuthClient?: boolean;
  errors?: HttpClientErrorHandlers;
}

const defaultNetworkMessage = "Failed to reach server";
const defaultSetupMessage = "Failed to send request";

export function createHttpClient({
  baseURL,
  withNoAuthClient = false,
  errors = {},
}: CreateHttpClientOptions) {
  const axiosInstance = axios.create({ baseURL });
  const noAuthAxiosInstance = withNoAuthClient
    ? axios.create({ baseURL })
    : undefined;

  const requestRefresh: TokenRefreshRequest = async (
    refreshToken: string
  ): Promise<IAuthTokens | string> => {
    const response = await axios.post(
      `${baseURL}/auth/refresh`,
      {},
      {
        headers: { Authorization: `Bearer ${refreshToken}` },
      }
    );
    return {
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    };
  };

  applyAuthTokenInterceptor(axiosInstance, {
    requestRefresh,
    getStorage: getBrowserLocalStorage,
  });

  /** Fresh access token for non-axios callers (e.g. fetch-based SSE). */
  const ensureAccessToken = async (): Promise<string | undefined> => {
    return refreshTokenIfNeeded(requestRefresh);
  };

  const apiRequest = async <T>(
    route: string,
    request: RequestData
  ): Promise<T> => {
    if (request.noAuth && !noAuthAxiosInstance) {
      throw new Error(
        "createHttpClient: noAuth request requires withNoAuthClient: true"
      );
    }

    const isFileUpload = request.data instanceof FormData;
    const options: AxiosRequestConfig = {
      data: request.data,
      params: request.params ?? {},
      method: request.method,
      url: route,
      headers: isFileUpload
        ? { "Content-Type": "multipart/form-data", ...request.headers }
        : { "Content-Type": "application/json", ...request.headers },
      responseType: request.responseType,
    };

    const client = request.noAuth
      ? noAuthAxiosInstance!
      : axiosInstance;

    try {
      const response = await client.request(options);
      return response.data;
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      if (error.response) {
        const message =
          error.response.data?.message ?? "Request failed";
        errors.onResponseError?.(message);
        throw new Error(message);
      }
      if (error.request) {
        errors.onNetworkError?.(defaultNetworkMessage);
        throw new Error(defaultNetworkMessage);
      }
      errors.onSetupError?.(defaultSetupMessage);
      throw new Error(defaultSetupMessage);
    }
  };

  const fetcher = <T>(args: [string, Record<string, string | number>?]): Promise<T> =>
    apiRequest(args[0], { method: "GET", params: args[1] });

  return {
    axiosInstance,
    ...(noAuthAxiosInstance ? { noAuthAxiosInstance } : {}),
    apiRequest,
    fetcher,
    ensureAccessToken,
  };
}
